import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { action, observable, computed, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  type ScopeChangeInventoryParams,
  type ScopeChangeParams,
} from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import { validateElements, mustHaveMaxLength } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import { type ListInventoryItemsForProviderFn } from '@/features/inventory'
import type { InventoryItemForProvider } from '@/features/inventory/InventoryItemForProvider'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import type { Id } from '@/infra/id'
import { ViewModelList } from '@/infra/view-model-list'
import { type Job } from '@/features/jobs/Job'
import { ScopeChangeCustomItemViewModel } from '@/features/jobs/details/components/RequestScopeChangesDialog/ListItems/ScopeChangeCustomItem/ScopeChangeCustomItemViewModel'
import { ScopeChangeInventoryItemViewModel } from '@/features/jobs/details/components/RequestScopeChangesDialog/ListItems/ScopeChangeInventoryItem/ScopeChangeInventoryItemViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { switchMap } from 'rxjs'
import { shareLatest, whileActive, withAbortSignal } from '@/utils/rx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * The step the request scope changes dialog is on.
 */
export type RequestScopeChangesState =
  /**
   * Waiting for the user to fill in all the scope changes required.
   */
  | 'RequestingChanges'
  /**
   * Waiting for the user to update all the quantities.
   */
  | 'UpdatingQuantity'
  /**
   * Waiting for the user to confirm that they want to request scope changes.
   */
  | 'ReviewingChanges'
  /**
   * CS will be notified when the request is sent.
   */
  | 'Success'
  /**
   * Something went wrong while executing the request scope changes command, could be a
   * validation error or something transient.
   */
  | 'Failed'

/**
 * Options for {@link RequestScopeChangesDialogViewModel}.
 */
export interface RequestScopeChangesDialogOptions {
  job: Job
  workspaceId: Id
  searchInventoryItemsForProvider: ListInventoryItemsForProviderFn
  getCurrentMember: GetCurrentMemberFn
  requestScopeChanges: (options: RequestScopeChangesOptions) => Promise<unknown>
  createAlertRetryWrapper: AlertRetryWrapperFactory
}

/**
 * The scope changes the user asks for.
 */
export interface RequestScopeChangesOptions {
  reason: string
  items: ScopeChangeParams[]
  inventoryItems: ScopeChangeInventoryParams[]
}

/**
 * View Model for initiating a scope change request.
 */
export class RequestScopeChangesDialogViewModel implements ActivationLifecycle {
  /**
   * The maximum length of the reason.
   */
  static readonly maxReasonLength = 280

  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Scope change inventory items view models.
   */
  private readonly _scopeChangeInventoryItems

  private readonly _workspaceId: Id
  private readonly _searchInventoryItemsForProvider: ListInventoryItemsForProviderFn
  private readonly _getCurrentMember: GetCurrentMemberFn
  private readonly _requestScopeChanges: RequestScopeChangesDialogOptions['requestScopeChanges']
  private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory

  /**
   * The job the scope changes are requested for.
   */
  readonly job: Job

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * The step the dialog is on.
   */
  @observable accessor state: RequestScopeChangesState = 'RequestingChanges'

  /**
   * Custom scope changes view models
   */
  @observable
  accessor scopeChangeCustomItems: ScopeChangeCustomItemViewModel[] = []

  /**
   * The error.
   */
  @observable accessor error = ''

  /**
   * The reason.
   */
  @observable accessor reason = ''

  /**
   * Search term for item selection
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Selected scope change inventory items
   */
  @observable
  accessor selectedInventoryItems: ScopeChangeInventoryItemViewModel[] = []

  /**
   * Inventory item scroll provider
   */
  readonly inventoryScrollProvider =
    new InfiniteScrollProvider<InventoryItemForProvider>({
      limit: 100,
      fetchItems: ({ paging, signal }) => {
        const withRetry = this._createAlertRetryWrapper(
          'Loading services failed',
          signal,
        )

        return withRetry(() =>
          this._searchInventoryItemsForProvider(
            {
              workspaceId: this._workspaceId,
              limit: paging.limit,
              searchText: this.searchText.value,
              after: paging.after ?? undefined,
              priceTypes: ['Flat'],
              archivedStatus: 'Active',
              proVisibilityStatus: 'Visible',
              jobType: this.job.jobType,
              orderBy: 'name',
            },
            signal,
          ),
        )
      },
    })

  /**
   * Server query pipeline.
   */
  readonly query$ = this.searchText.debouncedValue$.pipe(
    switchMap(() =>
      withAbortSignal((signal) =>
        // The loader renders a failure from the scroller's state.
        this.inventoryScrollProvider.fetch({ signal }).catch(() => undefined),
      ),
    ),
    shareLatest(),
  )

  /**
   * Validation context for scope changes step.
   */
  readonly scopeChangesValidationStep = validationContext(this, {
    scopeChangeCustomItems: [
      validateElements('Some scope changes are invalid.'),
    ],
  })

  /**
   * Validation context for submission step.
   */
  readonly submissionValidationStep = validationContext(this, {
    reason: [
      required('Reason is required.'),
      mustHaveMaxLength(
        RequestScopeChangesDialogViewModel.maxReasonLength,
        'Reason must be less than 280 characters.',
      ),
    ],
  })

  constructor(options: RequestScopeChangesDialogOptions) {
    this.job = options.job
    this._workspaceId = options.workspaceId
    this._searchInventoryItemsForProvider =
      options.searchInventoryItemsForProvider
    this._getCurrentMember = options.getCurrentMember
    this._requestScopeChanges = options.requestScopeChanges
    this._createAlertRetryWrapper = options.createAlertRetryWrapper
    this._scopeChangeInventoryItems = new ViewModelList({
      models: () => this.inventoryScrollProvider.items.slice(),
      create: (model) =>
        new ScopeChangeInventoryItemViewModel(
          model,
          this.job.jobScopeItems,
          this.selectedInventoryItems
            .map((i) => i.versionId)
            .includes(model.versionId),
          (item) => {
            if (item.selected) {
              this.selectedInventoryItems = [
                item,
                ...this.selectedInventoryItems,
              ]
            } else {
              this.selectedInventoryItems = this.selectedInventoryItems.filter(
                (i) => i.versionId !== item.versionId,
              )
            }
          },
        ),
    })
    this._lifecycle.manage(this.query$.pipe(whileActive(this.modal.showing$)))
    this._lifecycle.manage(this._scopeChangeInventoryItems)
  }

  /**
   * Whether any item was picked or added.
   */
  @computed
  get itemsSelected() {
    return (
      this.scopeChangeCustomItems.length !== 0 ||
      this.selectedInventoryItems.length !== 0
    )
  }

  /**
   * Whether the picked items change the job scope.
   */
  @computed
  get quantitiesChanged() {
    return (
      (this.selectedInventoryItems.length > 0 &&
        this.selectedInventoryItems.some(
          (item) => item.newQuantity !== item.currentQuantity,
        )) ||
      (this.scopeChangeCustomItems.length > 0 &&
        this.scopeChangeCustomItems.every((item) => item.quantity > 0))
    )
  }

  /**
   * Whether the current member can request custom items.
   */
  @computed
  get canCreateCustomJobScopeItemRequest() {
    const { permissions } = this._getCurrentMember()
    return (
      permissions.adminJobScopeChangeManage &&
      permissions.adminJobScopeChangeResolve
    )
  }

  /**
   * Whether to offer adding a custom item because the search found no
   * service items.
   */
  @computed
  get canShowAddCustomItemEmptyState() {
    return (
      this.isServiceItemsEmpty &&
      this.scopeChangeCustomItems.length === 0 &&
      this.searchText.value !== '' &&
      this.inventoryScrollProvider.isDone &&
      this.canCreateCustomJobScopeItemRequest
    )
  }

  /**
   * Total count for selected inventory items including custom items.
   */
  @computed
  get totalSelectedInventoryItemsCount() {
    return (
      this.selectedInventoryItems.length + this.scopeChangeCustomItems.length
    )
  }

  /**
   * The selected inventory items that have updated quantities
   */
  @computed
  get relevantSelectedInventoryItems() {
    return this.selectedInventoryItems.filter(
      (i) => i.currentQuantity !== i.newQuantity,
    )
  }

  /**
   * Indicates if the service items are empty.
   */
  @computed
  get isServiceItemsEmpty() {
    return this.serviceItems.length === 0
  }

  /**
   * The service items
   */
  @computed
  get serviceItems() {
    return this._scopeChangeInventoryItems.items
  }

  /**
   * Reason text box characters.
   */
  @computed
  get reasonCharsRemaining() {
    if (this.reason) {
      return (
        RequestScopeChangesDialogViewModel.maxReasonLength - this.reason.length
      )
    }

    return RequestScopeChangesDialogViewModel.maxReasonLength
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the dialog from its first step.
   */
  @action.bound
  show() {
    this.reset()
    this.modal.show()
  }

  /**
   * Send scope change request.
   */
  request = task.resolved(async () => {
    if (!this.validateSubmissionStep()) {
      return
    }

    const items = this.scopeChangeCustomItems.map((item) => ({
      name: item.name,
      quantity: item.quantity,
    }))

    // Only request the job scope change for inventory items that have a different new quantity
    const inventoryItems = this.selectedInventoryItems
      .filter((item) => item.newQuantity !== item.currentQuantity)
      .map((item) => ({
        versionId: item.versionId,
        quantity: item.newQuantity,
      }))

    try {
      await this._requestScopeChanges({
        reason: this.reason,
        items,
        inventoryItems,
      })
      this.setState('Success')
    } catch (err) {
      runInAction(() => {
        this.error = (err as Error).message
        this.setState('Failed')
      })
    }
  })

  /**
   * Resets the state.
   */
  @action.bound
  reset() {
    this.request.reset()
    this.submissionValidationStep.reset()
    this.scopeChangesValidationStep.reset()
    this.setState('RequestingChanges')

    this.reason = ''
    this.error = ''
    this.searchText.clear()
    this.scopeChangeCustomItems = []
    this.selectedInventoryItems = []
  }

  /**
   * Indicates if the submission step is valid.
   */
  @action.bound
  validateSubmissionStep() {
    return this.submissionValidationStep.reset().validate().isValid
  }

  /**
   * Moves to the next step if valid.
   */
  @action.bound
  moveToSubmissionStep() {
    if (this.scopeChangesValidationStep.reset().validate().isValid) {
      this.setState('ReviewingChanges')
    }
  }

  /**
   * Moves to the next step if valid.
   */
  @action.bound
  moveToQuantityStep() {
    if (this.scopeChangesValidationStep.reset().validate().isValid) {
      this.setState('UpdatingQuantity')
    }
  }

  /**
   * Goes back to picking items.
   */
  @action.bound
  backToSelectionStep() {
    this.setState('RequestingChanges')
  }

  /**
   * Goes back to updating quantities.
   */
  @action.bound
  backToQuantityStep() {
    this.setState('UpdatingQuantity')
  }

  /**
   * Sets the request scope changes state.
   */
  @action.bound
  setState(state: RequestScopeChangesState) {
    this.state = state
  }

  /**
   * Adds a new custom scope change
   */
  @action.bound
  addScopeChange() {
    this.scopeChangeCustomItems.push(new ScopeChangeCustomItemViewModel())
  }

  /**
   * Removes a previously added custom scope change.
   */
  @action.bound
  removeScopeChange(key: string) {
    const pos = this.scopeChangeCustomItems.findIndex(
      (item) => item.key === key,
    )
    this.scopeChangeCustomItems.splice(pos, 1)
  }
}
